import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export type Crumb = { label: string; to?: string };

type BreadcrumbProps = {
  items: Crumb[];
  tone?: "light" | "dark";
};

export default function Breadcrumb({ items, tone = "dark" }: BreadcrumbProps) {
  const isLight = tone === "light";
  return (
    <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={`${item.label}-${index}`} className="flex items-center gap-2">
            {item.to && !isLast ? (
              <Link
                to={item.to}
                className={`cursor-pointer transition-colors ${
                  isLight ? "text-background-200/80 hover:text-accent-300" : "text-foreground-500 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <span className={isLight ? "text-background-50" : "text-foreground-800"}>{item.label}</span>
            )}
            {!isLast && (
              <i className={`ri-arrow-right-s-line ${isLight ? "text-background-200/50" : "text-foreground-300"}`}></i>
            )}
          </span>
        );
      })}
    </nav>
  );
}

type PageHeroProps = {
  eyebrow: string;
  title: ReactNode;
  description: string;
  crumbs: Crumb[];
  image: string;
  imageAlt: string;
  meta?: ReactNode;
};

export function PageHero({ eyebrow, title, description, crumbs, image, imageAlt, meta }: PageHeroProps) {
  return (
    <section className="relative flex min-h-[520px] w-full items-end overflow-hidden bg-foreground-950 md:min-h-[600px]">
      <img
        src={image}
        alt={imageAlt}
        title={`${imageAlt} - 独家爆料线索征集`}
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/80 to-foreground-950/45"></div>
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-foreground-950/70 to-transparent"></div>

      <div className="relative mx-auto w-full max-w-[1320px] px-5 pb-16 pt-32 md:px-8 md:pb-20 md:pt-40">
        <div className="max-w-3xl">
          <Breadcrumb items={crumbs} tone="light" />
          <p className="mt-6 flex items-center gap-2 text-xs font-medium tracking-[0.22em] text-accent-300">
            <span className="h-px w-8 bg-accent-400"></span>
            {eyebrow}
          </p>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {title}
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-background-200/85 md:text-base md:leading-8">
            {description}
          </p>
          {meta && <div className="mt-8">{meta}</div>}
        </div>
      </div>
    </section>
  );
}